'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var min = _interopDefault(require('ml-array-min'));
var max = _interopDefault(require('ml-array-max'));
var mlGsd = require('ml-gsd');
var jcampconverter = require('jcampconverter');
var sortX = _interopDefault(require('ml-array-xy-sort-x'));
var xyParser = require('xy-parser');

function getAnnotations(spectrum, options = {}) {
  const { fillColor = 'green', strokeColor = 'red', creationFct } = options;
  const peaks = spectrum.peaks;
  if (!peaks) return [];
  let annotations = peaks.map(peak => {
    var annotation = {
      line: 1,
      type: 'rect',
      strokeColor: strokeColor,
      strokeWidth: 0,
      fillColor: fillColor
    };
    if (creationFct) {
      creationFct(annotation, peak);
    }
    oneAnnotation(annotation, peak);
    return annotation;
  });

  return annotations;
}

function oneAnnotation(annotation, peak) {
  annotation.label = [
    {
      text: peak.kind,
      size: '18px',
      anchor: 'middle',
      color: 'red',
      position: {
        x: peak.wavelength,
        y: peak.intensity,
        dy: '-15px'
      }
    }
  ];
  annotation.position = [
    {
      x: peak.wavelength,
      y: peak.intensity,
      dy: '-10px',
      dx: '-1px'
    },
    {
      x: peak.wavelength,
      y: peak.intensity,
      dy: '-5px',
      dx: '1px'
    }
  ];
}

/**
 *
 * @param {Spectrum} spectrum
 * @param {object} peak
 */

function addPeak(spectrum, peak = {}) {
  if (!peak.wavelength) {
    throw new Error('addPeak: peak mush have wavelength property');
  }
  const { wavelength, intensity } = peak;

  for (let existing of spectrum.peaks) {
    if (Number(existing.wavelength) === wavelength) return existing;
  }
  spectrum.peaks.push({
    wavelength: wavelength,
    intensity: intensity,
    kind: getPeakKind(intensity, spectrum.minIntensity, spectrum.maxIntensity)
  });
  return peak;
}

function getPeakKind(intensity, minIntensity, maxIntensity) {
  var position = intensity / (maxIntensity - minIntensity);
  if (position < 0.33) {
    return 'w';
  } else if (position < 0.66) {
    return 'm';
  }
  return 'S';
}

/**
 *
 * @param {Spectrum} spectrum
 * @param {number} targetWavelength
 * @param {object} [options]
 * @param {number} [options.range=0] Search in a range around the targetWavelength
 * @param {boolean} [options.optimize=false] Search for the closest peak to the targetWavelength
 */

function peakPicking(spectrum, targetWavelength, options = {}) {
  const { range = 0, optimize = false } = options;

  // find the peak that is the closest to the click
  let bestPeak = getClosest(spectrum, targetWavelength);

  if (optimize) {
    findClosest(spectrum, bestPeak);
  } else if (range) {
    bestInRange(spectrum, bestPeak, targetWavelength, range);
  }

  return addPeak(spectrum, bestPeak);
}

function getClosest(spectrum, targetWavelength) {
  let bestPeak = {
    intensity: spectrum.intensity[0],
    wavelength: spectrum.wavelength[0],
    index: 0
  };

  let error = Math.abs(targetWavelength - bestPeak.wavelength);
  for (let i = 1; i < spectrum.wavelength.length; i++) {
    let newError = Math.abs(targetWavelength - spectrum.wavelength[i]);
    if (newError < error) {
      error = newError;
      setBestPeak(spectrum, bestPeak, i);
    }
  }
  return bestPeak;
}

function bestInRange(spectrum, bestPeak, targetWavelength, range) {
  // we search the minimum based on wavelength +/- range
  for (let i = 0; i < spectrum.wavelength.length; i++) {
    if (Math.abs(spectrum.wavelength[i] - targetWavelength) <= range) {
      if (spectrum.intensity[i] > bestPeak.intensity) {
        setBestPeak(spectrum, bestPeak, i);
      }
    }
  }
}

function findClosest(spectrum, bestPeak) {
  let index = bestPeak.index;
  let previousIndex;
  while (index !== previousIndex) {
    previousIndex = index;
    if (index > 0 && spectrum.intensity[index - 1] > bestPeak.intensity) {
      index--;
      setBestPeak(spectrum, bestPeak, index);
    } else if (
      index < spectrum.wavelength.length - 1 &&
      spectrum.intensity[index + 1] > bestPeak.intensity
    ) {
      index++;
      setBestPeak(spectrum, bestPeak, index);
    }
  }
}

function setBestPeak(spectrum, bestPeak, index) {
  bestPeak.index = index;
  bestPeak.wavelength = spectrum.wavelength[index];
  bestPeak.intensity = spectrum.intensity[index];
}

/**
 *
 * @param {*} spectrum
 * @param {object} [options={}]
 * @param {number} [options.fromWavelength=0]
 * @param {number} [options.toWavelength=5000]
 * @param {number} [options.noiseLevel=0.01] - Specify the level of the noise
 * @param {number} [options.minMaxRatio=0.05] - Threshold to determine if a given peak should be considered as a noise
 * @param {boolean} [options.replaceExisting=true] - Replace existing peaks
 */

function autoPeakPicking(spectrum, options = {}) {
  const {
    noiseLevel = 0.001,
    minMaxRatio = 0.05,
    fromWavelength = 0,
    toWavelength = 5000,
    replaceExisting = true
  } = options;

  let peaks = mlGsd.gsd(spectrum.wavelength, spectrum.intensity, {
    noiseLevel,
    minMaxRatio,
    realTopDetection: true,
    maxCriteria: true,
    smoothY: false,
    sgOptions: { windowSize: 7, polynomial: 3 }
  });

  peaks = peaks.filter(
    peak => peak.x >= fromWavelength && peak.x <= toWavelength
  );

  if (replaceExisting) {
    while (spectrum.peaks.length) {
      spectrum.peaks.pop();
    }
  }

  peakLoop: for (let peak of peaks) {
    for (let existing of spectrum.peaks) {
      if (Number(existing.wavelength) === Number(peak.x)) continue peakLoop;
    }
    addPeak(spectrum, {
      wavelength: peak.x,
      intensity: peak.y
    });
  }
}

/**
 * Class allowing manipulate one Raman spectrum
 * @class spectrum
 * @param {object} [json={}] - object containing a spectrum
 * @param {Array} [json.wavelength=[]] - wavelength
 * @param {Array} [json.intensity=[]] - intensity values
 */
class Spectrum {
  constructor(json) {
    this.wavelength = json.wavelength || [];
    this.intensity = json.intensity || [];
    this.peaks = [];
    check(this);
  }

  /**
   *
   * @param {Array} [peaks=[]] array of peaks. Peaks are composed of transmittance, wavelength, kind
   */
  setPeaks(peaks = []) {
    this.peaks = peaks;
  }

  peakPicking(targetWavelength, options = {}) {
    peakPicking(this, targetWavelength, options);
  }

  getData() {
    return { x: this.wavelength, y: this.intensity };
  }
}

Spectrum.prototype.getAnnotations = function(options) {
  return getAnnotations(this, options);
};

Spectrum.prototype.autoPeakPicking = function(options) {
  return autoPeakPicking(this, options);
};

function check(spectrum) {
  if (spectrum.wavelength.length > 0) {
    spectrum.minWavelength = min(spectrum.wavelength);
  }
  if (spectrum.wavelength.length > 0) {
    spectrum.maxWavelength = max(spectrum.wavelength);
  }
  if (spectrum.intensity.length > 0) {
    spectrum.minIntensity = min(spectrum.intensity);
  }
  if (spectrum.intensity.length > 0) {
    spectrum.maxIntensity = max(spectrum.intensity);
  }
}

/**
 * Creates a new Chromatogram element based in a JCAMP string
 * @param {string} jcamp - String containing the JCAMP data
 * @return {Spectrum} - New class element with the given data
 */
function fromJcamp(jcamp) {
  const data = jcampconverter.convert(jcamp, { xy: true });
  let spectrum = data.spectra[0].data[0];
  spectrum = sortX(spectrum);
  return new Spectrum({
    wavelength: spectrum.x,
    intensity: spectrum.y
  });
}

/**
 * Creates a new Chromatogram element based in a Txt string
 * @param {string} text - String containing the data as CSV or TSV
 * @param {object} [options] - Options object for the parser
 * @param {string} [options.kind] - Absorbance or Transmisstance
 * @return {Spectrum} - New class element with the given data
 */
function fromText(text, options = {}) {
  options = Object.assign({}, options, { arrayType: 'xxyy' });
  const data = xyParser.parseXY(text, options);
  let spectrum = sortX({ x: data[0], y: data[1] });

  return new Spectrum({
    wavelength: spectrum.x,
    intensity: spectrum.y
  });
}

exports.Spectrum = Spectrum;
exports.fromJcamp = fromJcamp;
exports.fromText = fromText;
